import axiosInstance from '@/api/axiosInstance'
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { AuthContext } from '@/contexts/auth-context'
import { useQuery } from '@tanstack/react-query'
import { useContext, useEffect, useState } from 'react'

function UserHistory() {
  const { auth } = useContext(AuthContext)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalRecords: 0,
    totalPages: 1,
  })

  // Fetch points history data
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['pointsHistoryData', pagination.page, pagination.limit],
    queryFn: async () => {
      const response = await axiosInstance.get('/web-api/points/history', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
        },
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      })
      return response.data
    },
  })

  useEffect(() => {
    if (data) {
      setPagination(prev => ({
        ...prev,
        totalRecords: data.pagination.totalRecords,
        totalPages: data.pagination.totalPages,
      }))
    }
  }, [data])


  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }))
  }

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({
      ...prev,
      limit: parseInt(newLimit, 10),
      page: 1,
    }))
  }

  const getImageUrl = (path) => {
    return path ? `${import.meta.env.VITE_BASE_URL}${path}` : '/customer-assets/BLogo.svg'
  }

  return (
    <div className="container flex flex-col gap-4 p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Loyalty Point History</h3>
        <select
          value={pagination.limit}
          onChange={(e) => handleLimitChange(e.target.value)}
          className="text-sm border rounded-md px-2 focus:outline-none focus:ring-1 focus:ring-gray-400 "
        >
          <option value="10">10 per page</option>
          <option value="25">25 per page</option>
          <option value="50">50 per page</option>
        </select>
      </div>

      {isLoading ? (
        <div className="animate-pulse space-y-2">
          {[...Array(pagination.limit)].map((_, i) => (
            <div key={i} className="h-12 bg-gray-100 rounded"></div>
          ))}
        </div>
      ) : error ? (
        <div className="text-center p-4 border border-red-200 rounded bg-red-50">
          <p className="text-red-600">Failed to load history</p>
          <Button 
            onClick={refetch} 
            size="sm" 
            className="mt-2"
          >
            Retry
          </Button>
        </div>
      ) : data?.data?.length === 0 ? (
        <div className="text-center p-4 border rounded bg-gray-50">
          <p className="text-gray-600">No points history found</p>
        </div>
      ) : (
        <>
          <div className=" rounded-lg overflow-hidden">
            <Table>
              <TableHeader className=" text-gray-700 text-lg font-semibold">
                <TableRow>
                  <TableHead>Store</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Points</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.data?.map((history) => (
                  <TableRow key={history._id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full overflow-hidden border">
                          <img
                            src={getImageUrl(history.storeLogo)}
                            alt="logo"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <span>{history.storeName}</span>
                      </div>
                    </TableCell>
                    <TableCell>{history.date}</TableCell>
                    <TableCell>{history.points}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{history.reason}</TableCell>
                    <TableCell className="text-right">
                      <img 
                        src={history.type === "credit" 
                          ? "/customer-assets/credited.svg" 
                          : "/customer-assets/debited.svg"} 
                        alt={history.type} 
                        className="inline-block h-5"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {pagination.page} of {pagination.totalPages}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1 || isLoading}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages || isLoading}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default UserHistory